/**
 * What kind of food something is, without asking a model.
 *
 * Glitimė needs to react to *what* was eaten sometimes, not only its name and
 * kcal — a repeat-food callback, a food-triggered easter egg, a Wrapped
 * superlative that lands differently for a week of vegetables than for a week
 * of kebabs. None of that needs to be exact: a deterministic, hand-tuned
 * keyword match that occasionally shrugs is worth more here than a slow,
 * costly model call that occasionally hallucinates a class for "Duona." Names
 * this doesn't recognise get no class at all — she never guesses.
 */

import { foldLt } from './lithuanian.js'

export type FoodComedyClass =
  | 'saldumynai'
  | 'kepta'
  | 'mesa'
  | 'zuvis'
  | 'pieno'
  | 'darzoves'
  | 'gerimai'
  | 'sveikuoliska'
  | 'kruopos'

interface ClassRule {
  foodClass: FoodComedyClass
  /** Folded (diacritic/case-free) substrings — see `foldLt`. */
  keywords: readonly string[]
}

/**
 * Checked in order, first match wins — so a rule for something more specific
 * has to come before the general one it would otherwise be swallowed by
 * („varškės sūrelis" is a sweet before it is a dairy product).
 */
const RULES: readonly ClassRule[] = [
  {
    foodClass: 'saldumynai',
    keywords: [
      'varskes surel',
      'sausain',
      'saldain',
      'sokolad',
      'tort',
      'pyrag',
      'keks',
      'ledai',
      'desert',
      'vaflis',
      'vafli',
      'zefyr',
      'marmelad',
      'sakotis',
      'medus',
      'uogien',
      'spurg',
      'napoleon',
    ],
  },
  {
    foodClass: 'kepta',
    keywords: [
      'kebab',
      'bulvyt',
      'frit',
      'burger',
      'hot dog',
      'pica',
      'greitas maist',
      'kroketai',
      'nagets',
      'kepta',
      'gruzdint',
    ],
  },
  {
    foodClass: 'zuvis',
    keywords: ['zuv', 'lasis', 'silke', 'tunas', 'krevet', 'kalmar'],
  },
  {
    foodClass: 'mesa',
    keywords: [
      'vistien',
      'jautien',
      'kiaulien',
      'kalakut',
      'desr',
      'lasin',
      'kumpis',
      'cepelin',
      'kotlet',
      'karbonad',
      // Folded, so no keyword here may carry a diacritic: „faršas" would never
      // match anything `foldLt` produces.
      'fars',
    ],
  },
  {
    foodClass: 'pieno',
    keywords: ['pienas', 'jogurt', 'suris', 'varske', 'grietin', 'kefyr'],
  },
  {
    foodClass: 'darzoves',
    keywords: [
      'darzov',
      'salot',
      'agurk',
      'pomidor',
      'morka',
      'kopust',
      'brokol',
      'spanguol',
      'vaisi',
      'obuol',
      'bananas',
    ],
  },
  {
    foodClass: 'gerimai',
    keywords: ['alus', 'vynas', 'degtin', 'kokteil', 'sultys', 'limonad', 'gira', 'energini'],
  },
  {
    foodClass: 'sveikuoliska',
    keywords: ['avizin', 'chia', 'quinoa', 'kinoja', 'tofu', 'proteino batonel', 'proteinin'],
  },
  {
    foodClass: 'kruopos',
    keywords: ['duona', 'makaron', 'ryzi', 'grikiai', 'bulv', 'grudai'],
  },
]

/** The best class a bare name suggests, or null when nothing matches at all. */
export function classifyFoodName(name: string): FoodComedyClass | null {
  const folded = foldLt(name)
  for (const rule of RULES) {
    if (rule.keywords.some((keyword) => folded.includes(keyword))) return rule.foodClass
  }
  return null
}

/**
 * A short, class-aware aside for a Wrapped superlative — "the most-logged
 * thing this week was X, Y times" already has the name and the count; this
 * is what she adds once she knows what *kind* of thing it was. Unclassified
 * names get an empty string, which the caller drops rather than pads with a
 * guess.
 */
export function classAside(foodClass: FoodComedyClass | null, count: number): string {
  if (foodClass == null || count < 2) return ''
  switch (foodClass) {
    case 'saldumynai':
      return 'Saldi ištikimybė.'
    case 'kepta':
      return 'Kulinarinė monogamija.'
    case 'mesa':
      return 'Baltymų ištikimybė.'
    case 'zuvis':
      return 'Vandenyno abonementas.'
    case 'pieno':
      return 'Pieninė rutina.'
    case 'darzoves':
      return 'Įtartinai sąžininga savaitė.'
    case 'gerimai':
      return 'Skysta tradicija.'
    case 'sveikuoliska':
      return 'Disciplina, dokumentuota.'
    case 'kruopos':
      return 'Anglevandenilių pastovumas.'
  }
}
